import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { Subscription, takeUntil, asyncScheduler, Subject } from 'rxjs';
import {
  TocMenuItemLike,
  TocMenuItemsDefault,
} from '../../../../models/toc-config';

import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { RandomUtils } from 'app/shared/utils/random.utils';
import {
  BasemapItemLike,
  TocMenuItemEnableLike,
  TocService,
} from '../../toc.service';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { FloatingMenuComponent } from '../../../floating-menu/floating-menu.component';
import { TranslationService } from '@services/translation.service';

const menuItemResultsName = RandomUtils.uniqId() + '-results';

@Component({
  selector: 'app-toc',
  templateUrl: 'toc.component.html',
  styleUrls: ['toc.component.scss'],
})
export class TocComponent implements OnInit, OnDestroy {
  @ViewChild(FloatingMenuComponent, { static: false })
  floatingMenuRef: FloatingMenuComponent;

  @ViewChild('defaultContentScreenTpl', { static: true })
  defaultContentScreenTpl: TemplateRef<any>;
  @ViewChild('basemapGalleryScreenTpl', { static: true })
  basemapGalleryScreenTpl: TemplateRef<any>;

  @ViewChild('basemapContainerEl', { static: false })
  basemapContainerElRef: ElementRef<HTMLDivElement>;

  get opened() {
    return this.tocService.opened;
  }

  set opened(value) {
    this.tocService.opened = value;
  }

  @Input()
  container: HTMLElement;

  @Input()
  mapView: __esri.MapView;

  get cardHeight() {
    const forbiddenSize = this.appConfig.siiaMode
      ? 20
      : this.appConfig.internalMode
      ? 20
      : 32.9;
    return `calc(100vh - ${forbiddenSize}rem)`;
  }

  isMobileScreen: boolean = false;

  searchText: string;
  searchTextToApply: string;
  private searchTextSubscription: Subscription;

  menuItems: TocMenuItemEnableLike[];

  menuItemSelected: TocMenuItemEnableLike;
  contentScreenTpl: TemplateRef<any>;
  dataContext: any;

  basemapSelectedIndex = 0;

  basemapList: BasemapItemLike[];

  get searchInputVisible() {
    return (
      !this.appConfig.siiaMode &&
      this.menuItemSelected?.name !== TocMenuItemsDefault.BasemapList
    );
  }

  get isFloatingMenuFixed() {
    return !!this.floatingMenuRef?.fixed;
  }

  set isFloatingMenuFixed(value) {
    if (this.floatingMenuRef) {
      this.floatingMenuRef.fixed = !!value;
    }
  }

  get searchPlaceholder() {
    return this.ts?.getCurrentLanguage()?.['search-placeholder'] || 'Search...';
  }

  get isBtnDisableLayersVisible() {
    if (!(this.appConfig.internalMode || this.appConfig.externalMode)) {
      return false;
    }

    if (
      this.menuItemSelected?.name === TocMenuItemsDefault.BasemapList ||
      this.menuItemSelected?.name === menuItemResultsName
    ) {
      return false;
    }

    return true;
  }

  get isBtnDisableLayersDisabled() {
    if (this.menuItemSelected) {
      return !(
        this.menuItemSelected.name !== TocMenuItemsDefault.BasemapList &&
        this.menuItemSelected.enabled
      );
    }

    return !this.menuItems?.some((x) => {
      return x.name !== TocMenuItemsDefault.BasemapList && x.enabled;
    });
  }

  private lifecycle = new Subject<void>();

  constructor(
    private appConfig: AppConfigService,
    private tocService: TocService,
    private mainMapService: MainMapService,
    private screenService: ScreenService,
    public ts: TranslationService
  ) {}

  ngOnInit() {
    this.tocService
      .onLoadMenuItems()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((items) => {
        this.menuItems = items;
      });

    this.tocService
      .onLoadBasemapItems()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((items) => {
        this.basemapList = items;
      });

    this.tocService
      .onBasemapSelectedIndexChange()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((index) => {
        this.basemapSelectedIndex = index;
      });

    this.screenService
      .observeIsMobile()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        this.isMobileScreen = isMobile;
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onSearchTextChanged(text: string) {
    this.searchTextSubscription?.unsubscribe();
    this.searchTextSubscription = asyncScheduler.schedule(() => {
      this.searchTextToApply = text;

      if (
        !this.menuItemSelected ||
        this.menuItemSelected.name === menuItemResultsName
      ) {
        if (!this.menuItemSelected) {
          this.menuItemSelected = {
            name: menuItemResultsName,
            title: 'Resultados',
          };
        }

        if (text) {
          const layerList = this.tocService.getAllLayers();

          this.dataContext = {
            layerList,
          };

          this.contentScreenTpl = this.defaultContentScreenTpl;
        } else {
          this.onBtnGoBackClick();
        }
      }

      this.searchTextSubscription?.unsubscribe();
    }, 200);
  }

  onMenuItemClick(item: TocMenuItemLike) {
    this.menuItemSelected = item;

    if (item.name === TocMenuItemsDefault.BasemapList) {
      this.contentScreenTpl = this.basemapGalleryScreenTpl;
    } else {
      this.contentScreenTpl = this.defaultContentScreenTpl;

      this.dataContext = {
        menuItem: item,
      };
    }
  }

  onBtnGoBackClick() {
    this.searchTextSubscription?.unsubscribe();
    this.searchText = '';
    this.searchTextToApply = '';
    this.menuItemSelected = null;
    this.contentScreenTpl = null;
    this.dataContext = null;
  }

  onBtnDisableLayers() {
    const layers = this.menuItemSelected
      ? this.tocService.getLayerItemsByMenu(this.menuItemSelected)
      : this.tocService.getAllLayerItems();

    this.tocService.setRecursivelyLayersVisible(layers, false);
  }

  onBasemapItemSelected(item: BasemapItemLike) {
    this.mainMapService.setBasemapId(item?.id);
  }
}
